import React from "react";
import { Card, Descriptions, Space, Table, Tag, Typography } from "antd";
import { fmtDate } from "../institutionMerge.constants";

const { Text } = Typography;

/*
 * The permanent audit of what undid a merge: the full restore (`merge.restore`) and every
 * detached source (`merge.detached[]`). Who, when, why, which « à cheval » documents were
 * acknowledged (by whom, when), and the lists the operator saw at that moment — records left
 * untouched because they had changed (drift), records created on the target after the merge,
 * and the entangled bills / batches. Older merges stored counts instead of lists: both render.
 */

const list = x => (Array.isArray(x) ? x : []);
/** Table rows with a stable key (antd no longer supports the row index in `rowKey`). */
const keyed = xs => xs.map((x, i) => ({ ...x, _k: i }));
const countOf = (x, total) =>
  typeof total === "number"
    ? total
    : Array.isArray(x)
    ? x.length
    : typeof x === "number"
    ? x
    : 0;
const txt = x =>
  x && typeof x === "object"
    ? x.label || x.name || x.email || x._id || JSON.stringify(x)
    : x === undefined || x === null || x === ""
    ? "—"
    : String(x);

/** Label of a document: the entangled / drift / new lists carry one, the override only its id. */
const labelFinder = audit => {
  const byId = new Map();
  for (const x of [
    ...list(audit.entangledItems || audit.entangled),
    ...list(audit.driftItems || audit.drift),
    ...list(audit.newOnTargetItems || audit.newOnTarget),
  ])
    if (x && x.documentId && x.label) byId.set(String(x.documentId), x.label);
  return id => byId.get(String(id));
};

const ID_COL = {
  title: "ID",
  dataIndex: "documentId",
  render: v =>
    v ? (
      <Text copyable={{ text: String(v) }}>…{String(v).slice(-8)}</Text>
    ) : (
      "—"
    ),
};

function AuditBody({ audit, sourcesById }) {
  const labelOf = labelFinder(audit);
  const overrides = list(audit.overrides).map(o =>
    o && typeof o === "object" ? o : { documentId: o },
  );
  const drift = list(audit.driftItems || audit.drift);
  const fresh = list(audit.newOnTargetItems || audit.newOnTarget);
  const entangled = list(audit.entangledItems || audit.entangled);
  // relinkChoices: [{ documentId, label, sourceName, by, at }] (current); relink: { docId: sourceId } (older merges)
  const relink = Array.isArray(audit.relinkChoices)
    ? audit.relinkChoices.map(r => [
        r.label || r.documentId,
        r.sourceName || r.sourceId,
      ])
    : audit.relink && typeof audit.relink === "object"
    ? Object.entries(audit.relink).filter(([, v]) => v)
    : [];
  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Descriptions size="small" bordered column={2}>
        <Descriptions.Item label="Par">{audit.by || "—"}</Descriptions.Item>
        <Descriptions.Item label="Le">{fmtDate(audit.at)}</Descriptions.Item>
        <Descriptions.Item label="Raison" span={2}>
          {audit.reason ? (
            <Text>{audit.reason}</Text>
          ) : (
            <Text type="secondary">non renseignée</Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Remis en place">
          {audit.reverted ?? "—"}
        </Descriptions.Item>
        <Descriptions.Item label="Laissés (modifiés depuis)">
          {countOf(audit.drift, audit.driftTotal)}
        </Descriptions.Item>
        {("newOnTarget" in audit || "newOnTargetTotal" in audit) && (
          <Descriptions.Item label="Créés après la fusion">
            {countOf(audit.newOnTarget, audit.newOnTargetTotal)}
            {relink.length
              ? ` · ${relink.length} rattaché(s) à une source`
              : ""}
          </Descriptions.Item>
        )}
        {"keepTarget" in audit && (
          <Descriptions.Item label="Cible">
            {audit.keepTarget
              ? "conservée (résidu de fusion)"
              : "non conservée"}
          </Descriptions.Item>
        )}
      </Descriptions>

      <Table
        size="small"
        title={() => `Acquittements « à cheval » — ${overrides.length}`}
        pagination={false}
        rowKey="_k"
        dataSource={keyed(overrides)}
        locale={{ emptyText: "Aucun acquittement" }}
        columns={[
          { title: "Collection", dataIndex: "model", render: txt },
          {
            title: "Document",
            dataIndex: "documentId",
            render: id =>
              labelOf(id) || (
                <Text type="secondary">…{String(id).slice(-8)}</Text>
              ),
          },
          { title: "Acquitté par", dataIndex: "by", render: txt },
          { title: "Le", dataIndex: "at", render: v => (v ? fmtDate(v) : "—") },
        ]}
      />
      {drift.length > 0 && (
        <Table
          size="small"
          title={() => "Modifiés depuis la fusion — laissés tels quels"}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          rowKey="_k"
          dataSource={keyed(drift)}
          columns={[
            { title: "Collection", dataIndex: "model" },
            { title: "Enregistrement", dataIndex: "label", render: txt },
            { title: "Champ", dataIndex: "field", render: txt },
            {
              title: "Au moment de la restauration",
              dataIndex: "currentLabel",
              render: txt,
            },
            ID_COL,
          ]}
        />
      )}
      {fresh.length > 0 && (
        <Table
          size="small"
          title={() => "Créés sur la cible après la fusion"}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          rowKey="_k"
          dataSource={keyed(fresh)}
          columns={[
            { title: "Collection", dataIndex: "model" },
            { title: "Enregistrement", dataIndex: "label", render: txt },
            {
              title: "Rattaché à",
              render: (_, r) => {
                const to =
                  r.relinkedTo || (audit.relink && audit.relink[r.documentId]);
                return to ? (
                  (sourcesById.get(String(to)) || {}).name || txt(to)
                ) : (
                  <Text type="secondary">resté sur la cible</Text>
                );
              },
            },
            ID_COL,
          ]}
        />
      )}
      {entangled.length > 0 && (
        <Table
          size="small"
          title={() => "À cheval — factures / lots mêlant anciens et nouveaux"}
          pagination={false}
          rowKey="_k"
          dataSource={keyed(entangled)}
          columns={[
            { title: "Collection", dataIndex: "model" },
            { title: "N°", dataIndex: "label", render: txt },
            { title: "Statut", dataIndex: "status", render: txt },
            {
              title: "Gravité",
              dataIndex: "severity",
              render: s =>
                s === "blocking" ? (
                  <Tag color="red">bloquant</Tag>
                ) : (
                  <Tag color="orange">avertissement</Tag>
                ),
            },
            ID_COL,
          ]}
        />
      )}
    </Space>
  );
}

/** Restore + detach audit of one merge; renders nothing when neither happened. */
export function MergeAudit({ merge }) {
  const detached = list(merge.detached);
  if (!merge.restore && !detached.length) return null;
  const sourcesById = new Map(
    list(merge.sourceInstitutionIds).map(s => [String(s._id || s), s]),
  );
  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: "100%", marginTop: 16 }}
    >
      {merge.restore && (
        <Card size="small" title="Restauration — qui, quand, pourquoi">
          <AuditBody audit={merge.restore} sourcesById={sourcesById} />
        </Card>
      )}
      {detached.map((d, i) => (
        <Card
          key={`${d.sourceId}:${i}`}
          size="small"
          title={`Détachement — ${(sourcesById.get(String(d.sourceId)) || {})
            .name || d.sourceId}`}
        >
          <AuditBody audit={d} sourcesById={sourcesById} />
        </Card>
      ))}
    </Space>
  );
}

/**
 * « Comptes au moment de la validation » — the verification totals frozen by `validate`:
 * `{ verifiedAt, ok, residualTotal, danglingTotal, missing, forward: [{ key, expected, found }],
 * target: [{ key, count }] }`. Older merges stored a flat object or a list of `{ key, label, count }`.
 * Every value is a labelled count that wraps — never a raw JSON string, which cannot break and
 * used to stretch the page to several thousand pixels.
 */
const SNAPSHOT_LABELS = {
  verifiedAt: "Vérifiée le",
  ok: "Résultat",
  residualTotal: "Encore sur une source",
  danglingTotal: "Vers une adresse supprimée",
  missing: "Documents manquants",
};
const countText = v =>
  v === null || v === undefined
    ? "—"
    : typeof v === "number"
    ? v
    : Array.isArray(v)
    ? v.length
    : typeof v === "object"
    ? v.count ?? v.found ?? v.expected ?? "—"
    : String(v);
const wrapTag = {
  marginRight: 0,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
};

export function CountsSnapshot({ snapshot }) {
  if (!snapshot) return <Text type="secondary">—</Text>;
  if (Array.isArray(snapshot)) {
    if (!snapshot.length) return <Text type="secondary">—</Text>;
    return (
      <Space size={[4, 4]} wrap>
        {snapshot.map((x, i) => (
          <Tag key={x.key || i} style={wrapTag}>
            {x.label || x.key} : {countText(x.count ?? x.value)}
          </Tag>
        ))}
      </Space>
    );
  }
  const { forward, target, ...scalars } = snapshot;
  const totals = Object.entries(scalars);
  const fwd = list(forward);
  const tgt = list(target);
  if (!totals.length && !fwd.length && !tgt.length)
    return <Text type="secondary">—</Text>;
  return (
    <Space direction="vertical" size={6} style={{ width: "100%" }}>
      <Space size={[4, 4]} wrap>
        {totals.map(([k, v]) => (
          <Tag
            key={k}
            style={wrapTag}
            color={
              k === "ok"
                ? v
                  ? "green"
                  : "red"
                : typeof v === "number" && v > 0 && k !== "verifiedAt"
                ? "red"
                : undefined
            }
          >
            {SNAPSHOT_LABELS[k] || k} :{" "}
            {k === "verifiedAt"
              ? v
                ? fmtDate(v)
                : "—"
              : k === "ok"
              ? v
                ? "verte"
                : "pas verte"
              : countText(v)}
          </Tag>
        ))}
      </Space>
      {fwd.length > 0 && (
        <div>
          <Text type="secondary">Journal — attendu / trouvé : </Text>
          <Space size={[4, 4]} wrap>
            {fwd.map((f, i) => (
              <Tag
                key={f.key || i}
                style={wrapTag}
                color={f.expected === f.found ? "green" : "red"}
              >
                {f.label || f.key} : {countText(f.found)} /{" "}
                {countText(f.expected)}
              </Tag>
            ))}
          </Space>
        </div>
      )}
      {tgt.length > 0 && (
        <div>
          <Text type="secondary">Sur la cible : </Text>
          <Space size={[4, 4]} wrap>
            {tgt.map((t, i) => (
              <Tag key={t.key || i} style={wrapTag}>
                {t.label || t.key} : {countText(t.count)}
              </Tag>
            ))}
          </Space>
        </div>
      )}
    </Space>
  );
}
